import React, { useEffect, useState } from 'react'
import {
  Form,
  Button,
  Table,
  Row,
  Col,
  Input,
  Checkbox,
  Space,
  Select,
  Tag,
  Modal,
  Switch,
} from 'antd'
import { PlusOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons'
import AdminHeader from '../../CommonComponents/AdminHeader'
import AdminFooter from '../../CommonComponents/AdminFooter'
import axiosInstance from '../../../Utils/axiosInstance'
import { toast } from 'react-toastify'

// ======== API URLS (Change According To Your Backend) ========
import {
  getUser,
  saveUser,
  updateUser,
  getRoleList,
  getRoleMenusWithPages,
  getRoleAllocationList,
  getRoleAllocationById,
  updateUserStatus, // pass role_id
} from '../../APIComponents/AdminApi'

const { Option } = Select

const UserCreation = () => {
  //============================================
  const loginType = sessionStorage.getItem('loginType') // 'ADMIN' or 'USER'
  const menuData = JSON.parse(sessionStorage.getItem('menus')) || []

  const getPagePermission = () => {
    if (loginType === 'ADMIN') {
      return {
        is_new: 1,
        is_edit: 1,
        is_view: 1,
        is_delete: 1,
      }
    }

    for (const menu of menuData) {
      const page = menu.pages?.find((p) => p.page_name === 'User Creation')
      if (page) return page
    }

    return {
      is_new: 0,
      is_edit: 0,
      is_view: 0,
      is_delete: 0,
    }
  }

  const pagePermission = getPagePermission()
  const showActionColumn =
    loginType === 'ADMIN' ||
    pagePermission.is_edit == 1 ||
    pagePermission.is_view == 1 ||
    pagePermission.is_delete == 1

  //============================================

  const [form] = Form.useForm()

  const [userList, setUserList] = useState([])
  const [roleList, setRoleList] = useState([])
  const [tableData, setTableData] = useState([])

  const [showForm, setShowForm] = useState(false)
  const [editingUser, setEditingUser] = useState(null)

  const [loading, setLoading] = useState(false)
  const [submitLoading, setSubmitLoading] = useState(false)

  const [statusModalVisible, setStatusModalVisible] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [statusLoading, setStatusLoading] = useState(false)
  const [tempStatus, setTempStatus] = useState(null)
  const [viewModalVisible, setViewModalVisible] = useState(false)
  const [selectedViewUser, setSelectedViewUser] = useState(null)

  const handleView = (record) => {
    setSelectedViewUser(record)
    setViewModalVisible(true)
  }

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 5,
  })

  // =============================
  // Fetch Users
  // =============================
  const fetchUsers = async () => {
    try {
      const res = await axiosInstance.get(getUser)
      setUserList(res.data)
    } catch {
      toast.error('Failed to fetch users')
    }
  }

  // =============================
  // Fetch Roles
  // =============================
  const fetchRoles = async () => {
    try {
      const res = await axiosInstance.get(getRoleAllocationList)
      setRoleList(res.data)
    } catch {
      toast.error('Failed to fetch roles')
    }
  }

  useEffect(() => {
    fetchUsers()
    fetchRoles()
  }, [])

  // =============================
  // Fetch Role Permissions Table
  // =============================
  const fetchRolePermissions = async (roleId) => {
    try {
      setLoading(true)

      const res = await axiosInstance.get(`${getRoleAllocationById}/${roleId}`)

      //  IMPORTANT: use res.data.menus
      const formatted = (res.data.menus || []).map((menu) => ({
        key: `menu-${menu.menu_id}`,
        menu_id: menu.menu_id,
        menu_name: menu.menu_name,
        pages: (menu.pages || []).map((page) => ({
          key: `page-${page.page_id}`,
          page_id: page.page_id,
          page_name: page.page_name,
          is_view: page.is_view === 1,
          is_new: page.is_new === 1,
          is_edit: page.is_edit === 1,
          is_delete: page.is_delete === 1,
        })),
      }))

      setTableData(formatted)
    } catch (error) {
      toast.error('Failed to load permissions')
    } finally {
      setLoading(false)
    }
  }

  // =============================
  // Checkbox Handler
  // =============================
  const handleCheckboxChange = (menuId, pageId, type, checked) => {
    const updated = tableData.map((menu) => {
      if (menu.menu_id === menuId) {
        return {
          ...menu,
          pages: menu.pages.map((page) =>
            page.page_id === pageId ? { ...page, [type]: checked } : page,
          ),
        }
      }
      return menu
    })

    setTableData(updated)
  }

  // =============================
  // Submit
  // =============================
  const onFinish = async (values) => {
    const payload = {
      ...(editingUser && { user_id: editingUser }),
      username: values.username,
      login_id: values.login_id,
      password: values.password,
      role_id: values.role_id,
      status: editingUser
        ? selectedUser?.status // keep existing status on update
        : 1,
      menus: tableData.map((menu) => ({
        menu_id: menu.menu_id,
        pages: menu.pages.map((page) => ({
          page_id: page.page_id,
          is_view: page.is_view ? 1 : 0,
          is_new: page.is_new ? 1 : 0,
          is_edit: page.is_edit ? 1 : 0,
          is_delete: page.is_delete ? 1 : 0,
        })),
      })),
    }

    try {
      setSubmitLoading(true)

      if (editingUser) {
        const res = await axiosInstance.put(updateUser, payload)

        if (res.data === 'UPDATED_SUCCESS') {
          toast.success('Updated Successfully')
        } else if (res.data === 'ALREADYEXIST') {
          toast.error('Data already exist!')
        } else {
          toast.error('Update Failed')
        }
      } else {
        const res = await axiosInstance.post(saveUser, payload)

        if (res.data === 'ASSIGNPAGES') {
          toast.success('User Created Successfully')
        } else if (res.data === 'ALREADYEXIST') {
          toast.error('Data already exist!')
        } else {
          toast.error('Save Failed')
        }
      }

      setShowForm(false)
      setEditingUser(null)
      form.resetFields()
      setTableData([])
      fetchUsers()
    } catch {
      toast.error('Operation Failed')
    } finally {
      setSubmitLoading(false)
    }
  }

  // =============================
  // Edit User
  // =============================
  const handleEdit = async (record) => {
    setShowForm(true)
    setEditingUser(record.user_id)
    setSelectedUser(record)

    // Set basic fields
    form.setFieldsValue({
      username: record.username,
      login_id: record.login_id,
      role_id: record.role_id,
    })

    //  If user already has permission data
    if (record.menus && record.menus.length > 0) {
      const formatted = record.menus.map((menu) => ({
        key: `menu-${menu.menu_id}`,
        menu_id: menu.menu_id,
        menu_name: menu.menu_name,
        pages: (menu.pages || []).map((page) => ({
          key: `page-${page.page_id}`,
          page_id: page.page_id,
          page_name: page.page_name,
          is_view: page.is_view === 1,
          is_new: page.is_new === 1,
          is_edit: page.is_edit === 1,
          is_delete: page.is_delete === 1,
        })),
      }))

      setTableData(formatted)
    } else {
      // fallback → load role permissions
      await fetchRolePermissions(record.role_id)
    }
  }

  // =============================
  // Main Menu Columns
  // =============================
  const columns = [
    {
      title: 'Menu Name',
      dataIndex: 'menu_name',
    },
  ]

  // =============================
  // Expanded Submenu Table
  // =============================
  const expandedRowRender = (menu) => {
    const menuName = menu.menu_name?.toLowerCase()

    const isReport = menuName === 'report'
    const isAdmin = menuName === 'admin'

    const pageColumns = [{ title: 'Submenu Name', dataIndex: 'page_name' }]

    //  VIEW (Always visible)
    pageColumns.push({
      title: 'View',
      align: 'center',
      render: (_, record) => (
        <Checkbox
          checked={record.is_view}
          onChange={(e) =>
            handleCheckboxChange(
              menu.menu_id,
              record.page_id,
              'is_view',
              e.target.checked,
            )
          }
        />
      ),
    })

    //  NEW (Hide only for Report)
    if (!isReport) {
      pageColumns.push({
        title: 'New',
        align: 'center',
        render: (_, record) => (
          <Checkbox
            checked={record.is_new}
            onChange={(e) =>
              handleCheckboxChange(
                menu.menu_id,
                record.page_id,
                'is_new',
                e.target.checked,
              )
            }
          />
        ),
      })
    }

    //  EDIT (Hide only for Report)
    if (!isReport) {
      pageColumns.push({
        title: 'Edit',
        align: 'center',
        render: (_, record) => (
          <Checkbox
            checked={record.is_edit}
            onChange={(e) =>
              handleCheckboxChange(
                menu.menu_id,
                record.page_id,
                'is_edit',
                e.target.checked,
              )
            }
          />
        ),
      })
    }

    //  DELETE (Hide for Report & Admin)
    if (!isReport && !isAdmin) {
      pageColumns.push({
        title: 'Delete',
        align: 'center',
        render: (_, record) => (
          <Checkbox
            checked={record.is_delete}
            onChange={(e) =>
              handleCheckboxChange(
                menu.menu_id,
                record.page_id,
                'is_delete',
                e.target.checked,
              )
            }
          />
        ),
      })
    }

    return (
      <Table
        columns={pageColumns}
        dataSource={menu.pages}
        pagination={false}
        rowKey="page_id"
        bordered
      />
    )
  }

  // =============================
  // User List Columns
  // =============================
  const userColumns = [
    {
      title: 'Sr. No',
      key: 'index',
      render: (_, __, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
      width: '10%',
    },
    { title: 'Username', dataIndex: 'username' },
    { title: 'Login ID', dataIndex: 'login_id' },
    { title: 'Role', dataIndex: 'role_name' },
    {
      title: 'Status',
      render: (_, record) => (
        <Tag
          color={record.status === 1 ? 'green' : 'red'}
          style={{ cursor: 'pointer' }}
          onClick={() => {
            setSelectedUser(record)
            setTempStatus(record.status)
            setStatusModalVisible(true)
          }}
        >
          {record.status === 1 ? 'Active' : 'Blocked'}
        </Tag>
      ),
    },
    ...(showActionColumn
      ? [
          {
            title: 'Action',
            render: (_, record) => (
              <Space>
                {pagePermission.is_view == 1 && (
                  <Button
                    title="View"
                    icon={<EyeOutlined />}
                    onClick={() => handleView(record)}
                  />
                )}
                {pagePermission.is_edit == 1 && (
                  <Button
                    title="Edit"
                    icon={<EditOutlined />}
                    disabled={record.status === 2}
                    onClick={() => handleEdit(record)}
                  />
                )}
              </Space>
            ),
          },
        ]
      : []),
  ]

  const handleStatusSubmit = async () => {
    try {
      setStatusLoading(true)

      const payload = {
        status: tempStatus,
      }

      const res = await axiosInstance.put(
        `${updateUserStatus}/${selectedUser.user_id}`,
        payload,
      )

      if (res.data === 'UNBLOCKED') {
        toast.success('User Unblocked!')
        fetchUsers()
        setStatusModalVisible(false)
      } else if (res.data === 'BLOCKED') {
        toast.success('User Blocked!')
        fetchUsers()
        setStatusModalVisible(false)
      } else {
        toast.error('Update Failed')
      }
    } catch {
      toast.error('Error updating status')
    } finally {
      setStatusLoading(false)
    }
  }

  return (
    <>
      <AdminHeader />

      <div
        className="student-form-section container-fluid"
        style={{ minHeight: '100vh' }}
      >
        <div className="student-form-wrapper pb-5">
          <div className="row student-form-title">
            <h3 className="section-title1 text-start mb-2">User Creation</h3>
          </div>

          <div className="row student-form-container shadow p-4 rounded bg-white">
            {/* ================= USER LIST ================= */}
            {!showForm && (
              <>
                <Row className="mb-3">
                  <Col xs={24} className="text-end">
                    {pagePermission.is_new == 1 && (
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                          setShowForm(true)
                          setEditingUser(null)
                          form.resetFields()
                          setTableData([])
                        }}
                      >
                        Add
                      </Button>
                    )}
                  </Col>
                </Row>

                <Table
                  columns={userColumns}
                  dataSource={userList}
                  rowKey="user_id"
                  bordered
                  className="shadow rounded custom-table p-0"
                  style={{ overflowX: 'auto', border: '1px solid #dfdfdf' }}
                />
              </>
            )}

            {/* ================= USER FORM ================= */}
            {showForm && (
              <Form form={form} layout="vertical" onFinish={onFinish}>
                <Row gutter={16}>
                  <Col xs={24} md={6}>
                    <Form.Item
                      label="Username"
                      name="username"
                      rules={[{ required: true }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={6}>
                    <Form.Item
                      label="Login ID"
                      name="login_id"
                      rules={[{ required: true }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>

                  {!editingUser && (
                    <Col xs={24} md={6}>
                      <Form.Item
                        label="Password"
                        name="password"
                        rules={[{ required: true }]}
                      >
                        <Input.Password size="small" />
                      </Form.Item>
                    </Col>
                  )}

                  <Col xs={24} md={6}>
                    <Form.Item
                      label="Role"
                      name="role_id"
                      rules={[{ required: true }]}
                    >
                      <Select
                        placeholder="Select Role"
                        onChange={(value) => fetchRolePermissions(value)}
                      >
                        {roleList.map((role) => (
                          <Option key={role.role_id} value={role.role_id}>
                            {role.role_name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                {tableData.length > 0 && (
                  <Table
                    columns={columns}
                    dataSource={tableData}
                    expandable={{
                      expandedRowRender,
                      rowExpandable: (record) => record.pages.length > 0,
                    }}
                    pagination={{
                      current: pagination.current,
                      pageSize: pagination.pageSize,
                      total: roleList.length,
                      onChange: (page, pageSize) => {
                        setPagination({
                          current: page,
                          pageSize: pageSize,
                        })
                      },
                    }}
                    rowKey="menu_id"
                    bordered
                    className="shadow rounded custom-table p-0"
                    style={{ overflowX: 'auto', border: '1px solid #dfdfdf' }}
                  />
                )}

                <Row className="mt-4">
                  <Col xs={24} className="text-end">
                    <Space>
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={submitLoading}
                      >
                        {editingUser ? 'Update' : 'Submit'}
                      </Button>

                      <Button
                        onClick={() => {
                          setShowForm(false)
                          setEditingUser(null)
                        }}
                      >
                        Cancel
                      </Button>
                    </Space>
                  </Col>
                </Row>
              </Form>
            )}

            <Modal
              title="Change User Status"
              open={statusModalVisible}
              onCancel={() => setStatusModalVisible(false)}
              footer={null}
            >
              {selectedUser && (
                <div className="text-center">
                  <p>
                    Current Status:
                    <strong>
                      {selectedUser.status === 1 ? ' Active' : ' Blocked'}
                    </strong>
                  </p>

                  <div className="mb-5">
                    <Switch
                      checked={tempStatus === 1}
                      checkedChildren="Active"
                      unCheckedChildren="Blocked"
                      onChange={(checked) => setTempStatus(checked ? 1 : 2)}
                    />
                  </div>

                  <Space>
                    <Button onClick={() => setStatusModalVisible(false)}>
                      Cancel
                    </Button>
                    <Button
                      type="primary"
                      loading={statusLoading}
                      onClick={handleStatusSubmit}
                    >
                      Submit
                    </Button>
                  </Space>
                </div>
              )}
            </Modal>
          </div>

          <Modal style={{margin:"45px 0px"}}
            title={
              <div style={{ fontSize: '18px', fontWeight: 600 }}>
                👁 View User Details
              </div>
            }
            open={viewModalVisible}
            onCancel={() => setViewModalVisible(false)}
            footer={[
              <Button
                key="close"
                type="primary"
                onClick={() => setViewModalVisible(false)}
              >
                Close
              </Button>,
            ]}
            width={900}
            centered
          >
            {selectedViewUser && (
              <div
                style={{
                  background: '#f5f7fa',
                  padding: '20px',
                  borderRadius: '8px',
                }}
              >
                {/* Basic Details */}
                <Row gutter={[16, 16]}>
                  <Col span={6}>
                    <strong>Username :</strong>
                  </Col>
                  <Col span={18}>
                    <div className="view-box">{selectedViewUser.username}</div>
                  </Col>

                  <Col span={6}>
                    <strong>Login ID :</strong>
                  </Col>
                  <Col span={18}>
                    <div className="view-box">{selectedViewUser.login_id}</div>
                  </Col>

                  <Col span={6}>
                    <strong>Role :</strong>
                  </Col>
                  <Col span={18}>
                    <div className="view-box">{selectedViewUser.role_name}</div>
                  </Col>

                  <Col span={6}>
                    <strong>Status :</strong>
                  </Col>
                  <Col span={18}>
                    <Tag
                      color={selectedViewUser.status === 1 ? 'green' : 'red'}
                    >
                      {selectedViewUser.status === 1 ? 'Active' : 'Blocked'}
                    </Tag>
                  </Col>
                </Row>

                {/* Permissions */}
                {selectedViewUser.menus && selectedViewUser.menus.length > 0 && (
                  <>
                    <h4 style={{ marginTop: 20 }}>Permissions</h4>

                    <Table
                      dataSource={selectedViewUser.menus}
                      rowKey="menu_id"
                      pagination={false}
                      size="small"
                      bordered
                      columns={[
                        {
                          title: 'Menu',
                          dataIndex: 'menu_name',
                        },
                        {
                          title: 'Pages & Permissions',
                          render: (_, menu) => (
                            <div>
                              {menu.pages.map((page) => (
                                <div
                                  key={page.page_id}
                                  style={{
                                    marginBottom: '8px',
                                    padding: '8px',
                                    background: '#ffffff',
                                    borderRadius: '6px',
                                    border: '1px solid #e6e6e6',
                                  }}
                                >
                                  <strong>{page.page_name}</strong>

                                  <div style={{ marginTop: 5 }}>
                                    {page.is_view === 1 && (
                                      <Tag color="blue">View</Tag>
                                    )}
                                    {page.is_new === 1 && (
                                      <Tag color="green">New</Tag>
                                    )}
                                    {page.is_edit === 1 && (
                                      <Tag color="orange">Edit</Tag>
                                    )}
                                    {page.is_delete === 1 && (
                                      <Tag color="red">Delete</Tag>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ),
                        },
                      ]}
                    />
                  </>
                )}
              </div>
            )}
          </Modal>
        </div>
      </div>

      <AdminFooter />
    </>
  )
}

export default UserCreation
